import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n, LANG_OPTIONS } from '../contexts/I18nContext';
import { uiLangFor } from '../i18n';
import { ArrowRight, CaretDown } from '@phosphor-icons/react';

export default function Login() {
  const { user, checked, login, register, requestPasswordReset } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  if (checked && user) return <Navigate to="/" replace />;

  const switchMode = (next) => {
    setErr('');
    setSent(false);
    setMode(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (mode === 'forgot') {
      setLoading(true);
      await requestPasswordReset(email); // neutral response regardless of outcome
      setLoading(false);
      setSent(true);
      return;
    }
    setLoading(true);
    const res =
      mode === 'login'
        ? await login(email, password)
        : await register({ email, password, name, role: 'caregiver' });
    setLoading(false);
    if (!res.ok) setErr(res.error);
    else navigate('/');
  };

  const heading = mode === 'login' ? t('sign_in') : mode === 'register' ? t('create_account') : t('forgot_title');
  const subline = mode === 'login' ? t('login_sub') : mode === 'register' ? t('register_sub') : t('forgot_sub');

  return (
    <div className="min-h-screen grid lg:grid-cols-[5fr_6fr]">
      {/* Hero panel */}
      <div className="relative overflow-hidden hidden lg:flex flex-col justify-between p-12 bg-sage text-white">
        <div className="absolute inset-0 grain" />
        <div
          className="absolute -right-32 -bottom-32 w-[520px] h-[520px] rounded-full opacity-40"
          style={{ background: 'radial-gradient(circle, #C87961 0%, transparent 70%)' }}
        />
        <div
          className="absolute -left-16 -top-16 w-[320px] h-[320px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, #EDF2EE 0%, transparent 70%)' }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <img
            src="/favicon.png"
            alt={t('brand')}
            data-testid="login-logo-hero"
            className="w-10 h-10 rounded-xl bg-white/90 p-1 shadow-sm object-contain"
          />
          <div>
            <div className="font-heading text-2xl leading-none">{t('brand')}</div>
            <div className="text-xs text-white/70 mt-1">{t('tagline')}</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="relative w-fit">
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              data-testid="login-hero-heart"
              className="w-56 h-56 object-contain drop-shadow-2xl select-none pointer-events-none"
            />
            <h1 className="font-heading text-5xl leading-[1.05] tracking-tight -mt-16 relative">
              {mode === 'register' ? t('register_welcome') : t('login_welcome')}.
            </h1>
          </div>
          <p className="text-base text-white/80 max-w-sm leading-relaxed">
            {mode === 'register' ? t('register_sub') : t('login_sub')}
          </p>
        </div>

        <div className="relative z-10" />
      </div>

      {/* Form panel */}
      <div className="flex flex-col items-center justify-center p-6 md:p-12 bg-bg">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10 lg:hidden">
            <div className="flex items-center gap-2">
              <img
                src="/favicon.png"
                alt={t('brand')}
                data-testid="login-logo-mobile"
                className="w-8 h-8 rounded-lg bg-sage-100 p-1 object-contain"
              />
              <div className="font-heading text-xl">{t('brand')}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8">
            <div className="relative">
              <select
                value={uiLangFor(lang)}
                onChange={(e) => setLang(e.target.value)}
                data-testid="login-lang-select"
                aria-label={t('language')}
                className="appearance-none bg-bg-alt border border-stone_line text-ink-soft text-xs
                           rounded-full pl-3 pr-7 py-1 cursor-pointer
                           focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-sage-400
                           hover:text-ink transition-colors"
              >
                {LANG_OPTIONS.map((opt) => (
                  <option
                    key={opt.code}
                    value={opt.code}
                    data-testid={`login-lang-option-${opt.code}`}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
              <CaretDown
                size={10}
                weight="bold"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-soft"
              />
            </div>
          </div>

          <h2 className="font-heading text-3xl text-ink mb-2">
            {heading}
          </h2>
          <p className="text-sm text-ink-soft mb-8">
            {subline}
          </p>

          {mode === 'forgot' && sent ? (
            <div
              data-testid="forgot-success"
              className="text-sm text-sage-700 bg-sage-100 rounded-xl px-4 py-4 animate-fade-up"
            >
              {t('forgot_sent')}
            </div>
          ) : (
          <form onSubmit={submit} data-testid="login-form" className="space-y-4 animate-fade-up">
            {mode === 'register' && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-ink-muted font-semibold">
                  {t('full_name')}
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="login-name-input"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
                  placeholder="Anna Kalniņa"
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-ink-muted font-semibold">
                {t('email')}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
                placeholder="you@care.lv"
              />
            </div>
            {mode !== 'forgot' && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.2em] text-ink-muted font-semibold">
                  {t('password')}
                </label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    data-testid="forgot-password-link"
                    className="text-xs text-sage font-medium hover:text-sage-600"
                  >
                    {t('forgot_link')}
                  </button>
                )}
              </div>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 w-full px-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
                placeholder="••••••••"
              />
            </div>
            )}

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-terracotta-700 bg-terracotta-100 rounded-lg px-4 py-3"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full mt-2 bg-sage hover:bg-sage-600 text-white font-medium px-6 py-3.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t('loading') : mode === 'login' ? t('sign_in') : mode === 'register' ? t('create_account') : t('forgot_submit')}
              {!loading && <ArrowRight size={18} weight="bold" />}
            </button>
          </form>
          )}

          {mode === 'forgot' && (
            <div className="mt-8 text-sm text-ink-soft flex items-center gap-2">
              <button
                type="button"
                onClick={() => switchMode('login')}
                data-testid="back-to-login"
                className="text-sage font-medium hover:text-sage-600"
              >
                {t('forgot_back')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
